import React, { useCallback, useState } from 'react';
import { Modal, Slider, Button, Space, theme } from 'antd';
import Cropper, { Area, Point } from 'react-easy-crop';
import { IconRotate, IconRotateClockwise } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { message } from '../services/toastMessage';
import { getCroppedImageBlob } from '../helpers/cropImage';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface AvatarCropModalProps {
  open: boolean;
  imageSrc: string;
  onCancel: () => void;
  onSave: (file: File) => void;
}

/**
 * "Ajusta tu foto" step between picking a file and actually uploading it --
 * AvatarUpload.tsx opens this instead of uploading the raw file straight
 * away, so whatever the user picks (portrait, landscape, off-center,
 * sideways from a phone camera) ends up framed the way it'll actually be
 * shown: inside a circle. cropShape="round" only affects the preview mask;
 * the exported image is still a square (getCroppedImageBlob's output),
 * which every consumer already displays through a circular <Avatar>.
 */
const AvatarCropModal: React.FC<AvatarCropModalProps> = ({ open, imageSrc, onCancel, onSave }) => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { token } = theme.useToken();
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_croppedArea: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  // Reset so the next photo (a different file, or re-opening after Cancel)
  // doesn't inherit the previous one's pan/zoom/rotation.
  const resetAndClose = (after: () => void) => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    after();
  };

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels, rotation);
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      resetAndClose(() => onSave(file));
    } catch {
      message.error(translate(t, TRANSLATIONS.AVATAR_CROP_FAILED));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => resetAndClose(onCancel)}
      title={translate(t, TRANSLATIONS.AVATAR_CROP_TITLE)}
      width={isMobile ? '100%' : 420}
      style={isMobile ? { top: 0, maxWidth: '100%', margin: 0, paddingBottom: 0 } : undefined}
      styles={{
        content: {
          borderRadius: isMobile ? 0 : 20,
          border: '0.5px solid rgba(0,0,0,0.08)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          ...(isMobile ? { height: '100%' } : {}),
        },
      }}
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button shape="round" onClick={() => resetAndClose(onCancel)} disabled={saving}>
            {translate(t, TRANSLATIONS.AVATAR_CROP_CANCEL)}
          </Button>
          <Button type="primary" shape="round" onClick={handleSave} loading={saving}>
            {translate(t, TRANSLATIONS.AVATAR_CROP_SAVE)}
          </Button>
        </Space>
      }
    >
      <div style={{ position: 'relative', width: '100%', height: isMobile ? 'min(65vh, 420px)' : 320, background: '#00000010', borderRadius: 16, overflow: 'hidden' }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </div>

      <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button
          shape="circle"
          icon={<IconRotate size={18} />}
          onClick={() => setRotation((r) => r - 90)}
          aria-label={translate(t, TRANSLATIONS.AVATAR_CROP_ROTATE_LEFT)}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: token.colorTextSecondary, marginBottom: 2 }}>
            {translate(t, TRANSLATIONS.AVATAR_CROP_ZOOM_LABEL)}
          </div>
          <Slider min={1} max={3} step={0.05} value={zoom} onChange={setZoom} tooltip={{ open: false }} />
        </div>
        <Button
          shape="circle"
          icon={<IconRotateClockwise size={18} />}
          onClick={() => setRotation((r) => r + 90)}
          aria-label={translate(t, TRANSLATIONS.AVATAR_CROP_ROTATE_RIGHT)}
        />
      </div>
    </Modal>
  );
};

export default AvatarCropModal;
